import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ticketAPI, adminAPI } from "../services/api";
import { toast } from "sonner";
import {
  Clock,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Plus,
  Search,
  ArrowUpRight,
  Users,
  Zap,
  MoreVertical,
  ChevronRight,
  Tag,
  Loader2,
  BarChart3,
  PieChart,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recentTickets, setRecentTickets] = useState<any[]>([]);
  const [totalTicketsCount, setTotalTicketsCount] = useState(0);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [slaStats, setSlaStats] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [stats, setStats] = useState([
    {
      label: "Active Incidents",
      value: "0",
      change: "+0",
      trend: "up" as const,
      icon: AlertCircle,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
    },
    {
      label: "In Queue",
      value: "0",
      change: "+0",
      trend: "up" as const,
      icon: Clock,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Resolved Today",
      value: "0",
      change: "+0",
      trend: "up" as const,
      icon: CheckCircle,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    user?.role === "user"
      ? {
          label: "Resolution Rate",
          value: "0%",
          change: "0 resolved",
          trend: "up" as const,
          icon: TrendingUp,
          color: "text-purple-500",
          bgColor: "bg-purple-500/10",
        }
      : {
          label: "Avg. Response",
          value: "2.4h",
          change: "-18%",
          trend: "down" as const,
          icon: TrendingUp,
          color: "text-purple-500",
          bgColor: "bg-purple-500/10",
        },
  ]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const normalizePercentage = (value: unknown): number => {
    if (value == null || value === "") return 0;
    const numeric = Number(
      typeof value === "string" ? value.replace(/%/g, "").trim() : value,
    );
    if (Number.isNaN(numeric)) return 0;
    return numeric <= 1 ? numeric * 100 : numeric;
  };

  const normalizeHours = (value: unknown): number => {
    if (value == null || value === "") return 0;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const normalizeSlaConfig = (slaConfig: any) => {
    if (!slaConfig || typeof slaConfig !== "object") return null;

    return Object.fromEntries(
      Object.entries(slaConfig).map(([priority, rawConfig]: [string, any]) => {
        const config = rawConfig || {};

        return [
          priority,
          {
            ...config,
            firstResponseHours: normalizeHours(
              config.firstResponseHours ??
                config.first_response_hours ??
                config.firstResponseTargetHours ??
                config.first_response_target_hours ??
                config.firstResponse?.hours ??
                config.firstResponse?.targetHours,
            ),
            resolutionHours: normalizeHours(
              config.resolutionHours ??
                config.resolution_hours ??
                config.resolutionTargetHours ??
                config.resolution_target_hours ??
                config.resolution?.hours ??
                config.resolution?.targetHours,
            ),
            warningThreshold: normalizePercentage(
              config.warningThreshold ??
                config.warning_threshold ??
                config.warningPercent ??
                config.warning_percent,
            ),
          },
        ];
      }),
    );
  };

  const normalizeSlaStatistics = (data: any) => {
    const totalTicketsWithSLA = Number(
      data?.totalTicketsWithSLA ?? data?.totalTickets ?? 0,
    );
    const slaBreaches = Number(data?.slaBreaches ?? data?.breachedTickets ?? 0);
    const slaMetCount = Number(
      data?.slaMetCount ??
        data?.resolvedOnTime ??
        Math.max(totalTicketsWithSLA - slaBreaches, 0),
    );

    return {
      ...data,
      totalTicketsWithSLA,
      slaBreaches,
      slaMetCount,
      slaCompliance: normalizePercentage(
        data?.slaCompliance ?? data?.complianceRate,
      ),
      breachRate: normalizePercentage(data?.breachRate),
      slaConfig: normalizeSlaConfig(data?.slaConfig),
      resolution: {
        ...(data?.resolution || {}),
        compliance: normalizePercentage(
          data?.resolution?.compliance ??
            data?.resolutionCompliance ??
            data?.slaCompliance ??
            data?.complianceRate,
        ),
      },
      firstResponse:
        data?.firstResponse != null || data?.first_response != null
          ? {
              ...(data.firstResponse || data.first_response || {}),
              compliance: normalizePercentage(
                data?.firstResponse?.compliance ??
                  data?.first_response?.compliance ??
                  data?.firstResponseCompliance,
              ),
            }
          : null,
    };
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [
        ticketsResponse,
        analyticsResponse,
        slaStatsResponse,
        techResponse,
        reportsResponse,
      ] = await Promise.all([
        ticketAPI.list({ limit: 50 }),
        user?.role === "admin"
          ? adminAPI.analytics({ days: 30 })
          : Promise.resolve({ success: false }),
        user?.role === "admin"
          ? adminAPI.slaStatistics({ days: 30 })
          : Promise.resolve({ success: false }),
        user?.role === "admin"
          ? adminAPI.technicianStats()
          : Promise.resolve({ success: false }),
        user?.role === "admin"
          ? adminAPI.reports({})
          : Promise.resolve({ success: false }),
      ]);

      let ticketsList = [];
      if (ticketsResponse.success && ticketsResponse.data) {
        ticketsList =
          ticketsResponse.data.data ||
          ticketsResponse.data.items ||
          ticketsResponse.data.tickets ||
          [];
      } else if (Array.isArray(ticketsResponse)) {
        ticketsList = ticketsResponse;
      } else if (Array.isArray(ticketsResponse.data)) {
        ticketsList = ticketsResponse.data;
      }

      if (!Array.isArray(ticketsList)) {
        ticketsList = [];
      }

      setTotalTicketsCount(ticketsList.length);

      setRecentTickets(
        ticketsList.slice(0, 5).map((ticket: any) => ({
          id: ticket.id,
          ticketNumber: ticket.ticketNumber,
          title: ticket.title || "Untitled",
          description: ticket.description || "",
          status: ticket.status?.toLowerCase().replace(/_/g, "-") || "open",
          priority: ticket.priority?.toLowerCase() || "medium",
          time: ticket.createdAt ? formatTime(ticket.createdAt) : "Unknown",
          assignee:
            ticket.assignedTo?.fullName ||
            ticket.assignedTo?.name ||
            ticket.assignee?.fullName ||
            ticket.assignee?.name ||
            (ticket.assignedId ? "Assigned" : "Unassigned"),
          category: ticket.category?.name || "General",
        })),
      );

      const openCount = ticketsList.filter(
        (t: any) => t.status?.toUpperCase() === "OPEN",
      ).length;
      const assignedCount = ticketsList.filter(
        (t: any) =>
          t.status?.toUpperCase() === "ASSIGNED" ||
          t.status?.toUpperCase() === "IN_PROGRESS",
      ).length;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const resolvedToday = ticketsList.filter((t: any) => {
        const resolvedDate = new Date(t.resolvedAt || t.updatedAt);
        return t.status?.toUpperCase() === "RESOLVED" && resolvedDate >= today;
      }).length;

      let fourthStat: { value: string; change?: string } = { value: "N/A" };

      if (user?.role === "user") {
        // Resolution Rate for students
        const totalTickets = ticketsList.length;
        const resolvedTickets = ticketsList.filter(
          (t: any) =>
            t.status?.toUpperCase() === "RESOLVED" ||
            t.status?.toUpperCase() === "CLOSED",
        ).length;

        const rate =
          totalTickets > 0
            ? Math.round((resolvedTickets / totalTickets) * 100)
            : 0;
        fourthStat = {
          value: `${rate}%`,
          change: `${resolvedTickets} resolved`,
        };
      } else {
        const ticketsWithComments = ticketsList.filter(
          (t: any) => t.comments?.length > 0,
        );

        if (ticketsWithComments.length > 0) {
          const totalResponseTime = ticketsWithComments.reduce(
            (sum: number, ticket: any) => {
              const created = new Date(ticket.createdAt).getTime();
              const firstComment = ticket.comments[0];
              const responded = new Date(firstComment.createdAt).getTime();
              return sum + (responded - created);
            },
            0,
          );

          const avgMs = totalResponseTime / ticketsWithComments.length;
          const avgHours = Math.floor(avgMs / 3600000);
          const avgMinutes = Math.floor(avgMs / 60000);
          fourthStat = {
            value:
              avgHours > 0
                ? `${avgHours}h`
                : avgMinutes > 0
                  ? `${avgMinutes}min`
                  : "0min",
          };
        }
      }

      setStats((prev) => [
        { ...prev[0], value: String(openCount), change: `+${openCount}` },
        {
          ...prev[1],
          value: String(assignedCount),
          change: `+${assignedCount}`,
        },
        {
          ...prev[2],
          value: String(resolvedToday),
          change: `+${resolvedToday}`,
        },
        { ...prev[3], ...fourthStat },
      ]);

      if (reportsResponse?.success && reportsResponse?.data) {
        const avgRating =
          reportsResponse.data.feedbackStatistics?.averageRating ||
          reportsResponse.data.feedback?.averageRating ||
          0;
        const totalFeedback =
          reportsResponse.data.feedbackStatistics?.totalFeedback ||
          reportsResponse.data.feedback?.totalResponses ||
          0;

        setStats((prev) => [
          prev[0],
          prev[1],
          prev[2],
          {
            ...prev[3],
            value: avgRating > 0 ? `${avgRating.toFixed(1)}/5` : "N/A",
            change:
              totalFeedback > 0 ? `${totalFeedback} reviews` : "No reviews",
          },
        ]);
      }

      if (analyticsResponse?.success && analyticsResponse?.data) {
        setAnalytics(analyticsResponse.data);
      }

      if (slaStatsResponse?.success && slaStatsResponse?.data) {
        setSlaStats(normalizeSlaStatistics(slaStatsResponse.data));
      }

      if (techResponse?.success && techResponse?.data) {
        const techList = Array.isArray(techResponse.data)
          ? techResponse.data
          : [];

        setTeamMembers(
          techList.slice(0, 4).map((item: any) => ({
            name: item.technician?.fullName || item.fullName || "Unknown",
            status: item.technician?.isActive !== false ? "active" : "away",
            load: item.activeTickets || 0,
          })),
        );
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const statusColors = {
    open: "bg-red-500/10 text-red-400",
    "in-progress": "bg-blue-500/10 text-blue-400",
    pending: "bg-yellow-500/10 text-yellow-400",
    resolved: "bg-green-500/10 text-green-400",
  };

  const displayedTotalTickets = Math.max(
    totalTicketsCount,
    Number(slaStats?.totalTicketsWithSLA ?? 0),
  );
  const displayedBreaches = Math.min(
    Number(slaStats?.slaBreaches ?? 0),
    displayedTotalTickets,
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-full p-4 sm:p-6 md:p-8 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                Service Desk
              </h1>
              <span className="px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-medium bg-primary/10 text-primary rounded-full uppercase tracking-wider">
                {user?.role}
              </span>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              Real-time incident management and service operations
            </p>
          </div>
          <Link
            to="/dashboard/new-ticket"
            className="relative group inline-flex items-center gap-2 w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-primary/20 blur-lg group-hover:blur-xl transition-all" />
            <div className="relative bg-primary hover:bg-primary/90 text-white font-semibold px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg w-full sm:w-auto">
              <Plus className="w-4 h-4" />
              <span>New Incident</span>
            </div>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-card border border-border/50 rounded-xl p-6 hover:border-primary/30 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div
                      className={`flex items-center gap-1 text-xs font-medium ${
                        stat.trend === "down"
                          ? "text-green-400"
                          : "text-blue-400"
                      }`}
                    >
                      <span>{stat.change}</span>
                      <TrendingUp
                        className={`w-3 h-3 ${stat.trend === "down" ? "rotate-180" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground mb-1 font-mono">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="xl:col-span-2 space-y-4 sm:space-y-6"
          >
            <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-lg">
              <div className="border-b border-border/50 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground font-serif">
                    Active Queue
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                      onClick={() => setShowSearch(!showSearch)}
                    >
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {showSearch && (
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="search"
                      placeholder="Search tickets by title or ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  {["All", "Open", "In Progress", "Pending"].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                        filter === activeFilter
                          ? "bg-primary text-white"
                          : "bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              <div className="divide-y divide-border/50">
                {(() => {
                  let filtered = recentTickets;

                  if (activeFilter !== "All") {
                    const filterStatus = activeFilter
                      .toLowerCase()
                      .replace(/ /g, "-");
                    filtered = filtered.filter(
                      (ticket) => ticket.status === filterStatus,
                    );
                  }

                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase();
                    filtered = filtered.filter(
                      (ticket) =>
                        ticket.title.toLowerCase().includes(query) ||
                        ticket.id.toLowerCase().includes(query) ||
                        (ticket.description || "")
                          .toLowerCase()
                          .includes(query),
                    );
                  }

                  return filtered.length > 0 ? (
                    filtered.map((ticket, idx) => (
                      <motion.div
                        key={ticket.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 + idx * 0.05 }}
                        className="p-4 sm:p-6 hover:bg-muted/30 transition-all cursor-pointer group"
                        onClick={() =>
                          (window.location.href = `/dashboard/ticket/${ticket.id}`)
                        }
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div
                            className={`mt-1 w-1 h-12 sm:h-16 rounded-full flex-shrink-0 ${
                              ticket.priority === "critical"
                                ? "bg-red-500"
                                : ticket.priority === "high"
                                  ? "bg-orange-500"
                                  : ticket.priority === "medium"
                                    ? "bg-yellow-500"
                                    : "bg-blue-500"
                            }`}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-mono font-semibold text-primary">
                                  {ticket.ticketNumber || ticket.id}
                                </span>
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    statusColors[
                                      ticket.status as keyof typeof statusColors
                                    ]
                                  }`}
                                >
                                  {ticket.status.replace("-", " ")}
                                </span>
                              </div>
                              <button
                                className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-4 h-4 text-muted-foreground" />
                              </button>
                            </div>

                            <h3 className="text-sm sm:text-base font-semibold text-foreground mb-1">
                              {ticket.title}
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-1">
                              {ticket.description}
                            </p>

                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{ticket.assignee}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                <span>{ticket.category}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>{ticket.time}</span>
                              </div>
                            </div>
                          </div>

                          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-6 text-center">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">
                        {searchQuery || activeFilter !== "All"
                          ? "No tickets match your filters"
                          : "No tickets found"}
                      </p>
                    </div>
                  );
                })()}
              </div>

              <div className="border-t border-border/50 p-4 bg-muted/20">
                <Link
                  to="/dashboard/history"
                  className="w-full text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center justify-center gap-2"
                >
                  <span>View All Tickets</span>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4 sm:space-y-6"
          >
            {/* Team Status - Only show for admin */}
            {user?.role === "admin" && (
              <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-lg">
                <div className="border-b border-border/50 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-primary" />
                    <h3 className="font-bold text-foreground font-serif">
                      Team Status
                    </h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Current availability
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  {teamMembers.length > 0 ? (
                    teamMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <span className="text-xs font-semibold text-foreground">
                                {member.name
                                  .split(" ")
                                  .map((n: string) => n[0])
                                  .join("")}
                              </span>
                            </div>
                            <div
                              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${
                                member.status === "active"
                                  ? "bg-green-500"
                                  : "bg-yellow-500"
                              }`}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-foreground">
                              {member.name}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {member.status}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-mono font-semibold text-primary">
                          {member.load}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No team members available
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Performance - Only show for admin */}
            {user?.role === "admin" && (
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-foreground font-serif">
                    Performance
                  </h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">SLA Met</span>
                      <span className="font-semibold text-foreground">
                        {slaStats?.resolution?.compliance != null
                          ? `${Number(slaStats.resolution.compliance).toFixed(1)}%`
                          : slaStats?.slaCompliance != null
                            ? `${Number(slaStats.slaCompliance).toFixed(1)}%`
                            : "N/A"}
                      </span>
                    </div>
                    <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{
                          width: `${Math.min(
                            slaStats?.resolution?.compliance ??
                              slaStats?.slaCompliance ??
                              0,
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">
                        {slaStats?.firstResponse?.compliance != null
                          ? "First Response"
                          : "Avg Response"}
                      </span>
                      <span className="font-semibold text-foreground">
                        {slaStats?.firstResponse?.compliance != null
                          ? `${Number(slaStats.firstResponse.compliance).toFixed(1)}%`
                          : stats[3]?.value || "N/A"}
                      </span>
                    </div>
                    <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{
                          width:
                            slaStats?.firstResponse?.compliance != null
                              ? `${Math.min(slaStats.firstResponse.compliance, 100)}%`
                              : "87%",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {user?.role === "admin" && analytics?.trend && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-6"
          >
            <div className="bg-card border border-border/50 rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-foreground font-serif">
                  Ticket Trends (Last 30 Days)
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={analytics.trend}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorCreated"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorResolved"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      color: "hsl(var(--foreground))",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorCreated)"
                    name="Created"
                  />
                  <Area
                    type="monotone"
                    dataKey="resolved"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorResolved)"
                    name="Resolved"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border/50 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-6">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-foreground font-serif">
                    SLA Compliance
                  </h3>
                </div>
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        stroke="hsl(var(--muted))"
                        strokeWidth="16"
                        fill="none"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="80"
                        stroke="hsl(var(--primary))"
                        strokeWidth="16"
                        fill="none"
                        strokeDasharray={`${(slaStats?.slaCompliance || 0) * 5.024} 502.4`}
                        strokeLinecap="round"
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-4xl font-bold text-foreground">
                        {(slaStats?.slaCompliance || 0).toFixed(1)}%
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        SLA Met
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 grid grid-cols-2 gap-4 w-full">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">
                        Total Tickets
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {displayedTotalTickets}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Breaches</p>
                      <p className="text-lg font-bold text-red-400">
                        {displayedBreaches}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border/50 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-6">
                  <PieChart className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-foreground font-serif">
                    Priority Distribution
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <RechartsPie>
                    <Pie
                      data={[
                        {
                          name: "Critical",
                          value: recentTickets.filter(
                            (t) => t.priority === "critical",
                          ).length,
                          color: "#ef4444",
                        },
                        {
                          name: "High",
                          value: recentTickets.filter(
                            (t) => t.priority === "high",
                          ).length,
                          color: "#f97316",
                        },
                        {
                          name: "Medium",
                          value: recentTickets.filter(
                            (t) => t.priority === "medium",
                          ).length,
                          color: "#eab308",
                        },
                        {
                          name: "Low",
                          value: recentTickets.filter(
                            (t) => t.priority === "low",
                          ).length,
                          color: "#3b82f6",
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={false}
                    >
                      {[
                        {
                          name: "Critical",
                          value: recentTickets.filter(
                            (t) => t.priority === "critical",
                          ).length,
                          color: "#ef4444",
                        },
                        {
                          name: "High",
                          value: recentTickets.filter(
                            (t) => t.priority === "high",
                          ).length,
                          color: "#f97316",
                        },
                        {
                          name: "Medium",
                          value: recentTickets.filter(
                            (t) => t.priority === "medium",
                          ).length,
                          color: "#eab308",
                        },
                        {
                          name: "Low",
                          value: recentTickets.filter(
                            (t) => t.priority === "low",
                          ).length,
                          color: "#3b82f6",
                        },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry: any) => (
                        <span style={{ color: "hsl(var(--foreground))" }}>
                          {value}: {entry.payload.value}
                        </span>
                      )}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-card border border-border/50 rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-foreground font-serif">
                  Ticket Status Overview
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    {
                      name: "Open",
                      count: recentTickets.filter((t) => t.status === "open")
                        .length,
                      color: "#ef4444",
                    },
                    {
                      name: "In Progress",
                      count: recentTickets.filter(
                        (t) => t.status === "in-progress",
                      ).length,
                      color: "#3b82f6",
                    },
                    {
                      name: "Pending",
                      count: recentTickets.filter((t) => t.status === "pending")
                        .length,
                      color: "#eab308",
                    },
                    {
                      name: "Resolved",
                      count: recentTickets.filter(
                        (t) => t.status === "resolved",
                      ).length,
                      color: "#10b981",
                    },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="name"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      color: "hsl(var(--foreground))",
                    }}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {[
                      { color: "#ef4444" },
                      { color: "#3b82f6" },
                      { color: "#eab308" },
                      { color: "#10b981" },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
